import {
  Button,
  Center,
  Flex,
  Group,
  Menu,
  Modal,
  Paper,
  Stack,
  Text,
  Badge,
  useMantineTheme,
} from "@mantine/core";
import { UserStatus } from "../models/Message";
import { MdCheckCircle, MdDoNotDisturbOn } from "react-icons/md";
import { convertToLocalDateTime } from "../utilities/DateUtil";
import React, { FC } from "react";
import { ChatSessionValues } from "../hooks/useChatSessions";
import { ChatSession } from "../models/ChatSession";
import { useDisclosure } from "@mantine/hooks";
import { tokenUsername } from "../utilities/TokenUtil";

interface ChatSessionItemProps {
  sessionKey: string;
  shadow: string;
  background: string;
  isAssignedPanel: boolean;
  chatSessionValues: ChatSessionValues;
  chatSession: ChatSession;
  unreadCounts: Map<string, number>;
  lastMessages: Map<string, string>;
  sessionUserStatuses: Map<string, UserStatus>;
}

const ChatSessionItem: FC<ChatSessionItemProps> = ({
  sessionKey,
  shadow,
  background,
  isAssignedPanel,
  chatSessionValues,
  chatSession,
  unreadCounts,
  lastMessages,
  sessionUserStatuses,
}) => {
  const [opened, { open, close }] = useDisclosure(false);

  const theme = useMantineTheme();

  return (
    <>
      <Modal opened={opened} onClose={close} title="Unassign this session">
        <Stack>
          <Text>
            {tokenUsername() === "superuser"
              ? `Admin user '${chatSession.adminUserUsername}' will be unassigned from this session. Do you want to continue?`
              : "Are you sure you want to be unassigned from this session? Another\n" +
                "            admin user will be able to assign themself to this session."}
          </Text>
          <Group grow>
            <Button onClick={close}>Cancel</Button>
            <Button
              onClick={() => {
                chatSessionValues.giveUpAssignedSession(chatSession.sessionId);
                close();
              }}
              color={theme.colors.red[7]}
            >
              Unassign
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Paper
        className="session-button"
        p="md"
        maw="100%"
        key={sessionKey}
        shadow={shadow}
        bg={background}
        onClick={() => {
          chatSessionValues.switchActiveSession(chatSession, false);
        }}
      >
        <Stack gap="xs" maw="100%">
          <Group justify="space-between">
            <Text size="xs">User: {chatSession.userId}</Text>
            {isAssignedPanel && (
              <>
                {sessionUserStatuses.get(chatSession.sessionId) ===
                UserStatus.ACTIVE ? (
                  <Group>
                    <Text size="sm">Online </Text>
                    <MdCheckCircle color={theme.colors.green[7]} />
                  </Group>
                ) : (
                  <Group>
                    <Text size="sm">Away </Text>
                    <MdDoNotDisturbOn color={theme.colors.red[7]} />
                  </Group>
                )}
              </>
            )}
          </Group>
          <Group justify="space-between" maw="100%">
            <Stack gap="xs" maw="100%">
              {lastMessages.get(chatSession.sessionId)?.trim() === "" ? (
                <Text fs="italic" c={theme.colors.blue[8]}>
                  File attachment
                </Text>
              ) : lastMessages.get(chatSession.sessionId) != null ? (
                <Text fs="italic" maw="100%" className="truncate-message-text">
                  "{lastMessages.get(chatSession.sessionId)}"
                </Text>
              ) : (
                <Text fs="italic" c={theme.colors.red[5]}>
                  No messages in this session
                </Text>
              )}

              <Text size="xs">
                Created: {convertToLocalDateTime(chatSession.isoCreatedAt)}
              </Text>
              <Text size="xs">
                Last updated: {convertToLocalDateTime(chatSession.isoUpdatedAt)}
              </Text>
              <Badge color={chatSession.hasJobAlert ? "green" : "gray"}>Daily job alert {chatSession.hasJobAlert ? "enabled" : "disabled"}</Badge>
            </Stack>
            {unreadCounts.get(chatSession.sessionId) !== 0 &&
              chatSession.adminUserId != null && (
                <Paper
                  miw="45"
                  p="xs"
                  radius="xl"
                  bg={theme.colors.red[7]}
                  shadow="xs"
                >
                  <Center>
                    <Text fw={700} c="white" size="xs">
                      {unreadCounts.get(chatSession.sessionId)}
                    </Text>
                  </Center>
                </Paper>
              )}
          </Group>
          {chatSession.sessionId ===
            chatSessionValues.activeSession?.sessionId &&
            chatSession.adminUserId != null && (
              <Flex justify="flex-end">
                <Menu shadow="md">
                  <Menu.Target>
                    <Button w="auto" size="xs" color={theme.colors.gray[5]}>
                      ...
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item onClick={open}>Unassign this session</Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Flex>
            )}
          {chatSession.sessionId ===
            chatSessionValues.activeSession?.sessionId &&
            chatSession.adminUserId == null &&
            tokenUsername() !== "superuser" && (
              <Button
                onClick={() =>
                  chatSessionValues.takeOverUnassignedSession(chatSession)
                }
              >
                Assign this session to me
              </Button>
            )}
        </Stack>
      </Paper>
    </>
  );
};

export default ChatSessionItem;
